import { PageTitle } from 'components/PageTitle'
import { FullPageSpinner } from 'components/Spinner'
import { signInWithCustomToken } from 'firebase/auth'
import { useRootStore } from 'hooks/rootStore'
import { useEffect, useMemo, useState } from 'react'
import { useRepository } from '../../hooks/auth'
import { captureException } from '@sentry/react'
import { useTranslationTyped } from 'i18n/i18n'
import { useLTIMode } from 'hooks/use-lti-mode'
import { BreakoutButtonLink } from 'components/design-system/BreakoutButton'
import { InstitutionIntegrationType } from '@breakoutlearning/firebase-repository/types'
import { areWeInIframe } from 'util/areWeInIframe'

function TokenLogin() {
  // const [loggingIn, setLoggingIn] = useState(false)
  const rootStore = useRootStore()
  const repository = useRepository()
  const { tt } = useTranslationTyped()
  const [hasProcessedToken, setHasProcessedToken] = useState(false)

  const queryParams = new URLSearchParams(window.location.search)
  const token = queryParams.get('token')
  const deepLinkJwt = queryParams.get('deepLinkJwt')
  const redirectTo = queryParams.get('redirectTo')
  const landingPage = queryParams.get('landingPage')
  const ltiMode = queryParams.get('ltiMode')
  const impersonateUserId = queryParams.get('impersonateUserId')
  const sectionId = queryParams.get('sectionId')
  const organizationId = queryParams.get('organizationId')
  const placement = queryParams.get('placement')
  const { setLTIMode } = useLTIMode()

  const validatedLTIMode: InstitutionIntegrationType | null = useMemo(() => {
    if (
      ltiMode &&
      Object.values(InstitutionIntegrationType).includes(
        ltiMode as InstitutionIntegrationType
      )
    )
      return ltiMode as InstitutionIntegrationType
    return null
  }, [ltiMode])

  const ltiOptions: ReturnType<typeof useLTIMode>['ltiOptions'] =
    useMemo(() => {
      if (!validatedLTIMode) return null

      const options: typeof ltiOptions = {
        ltiMode: validatedLTIMode,
        placement: '',
      }

      if (sectionId) options.sectionId = sectionId
      if (organizationId) options.organizationId = organizationId
      if (placement) options.placement = placement

      return options
    }, [validatedLTIMode, sectionId, organizationId, placement])

  useEffect(() => {
    if (hasProcessedToken) return

    if (!token) {
      console.error('bad token')
      captureException(new Error('BadTokenError'))
      return
    }

    setHasProcessedToken(true)

    if (deepLinkJwt) {
      localStorage.setItem('deepLinkJwt', deepLinkJwt)
    }

    if (ltiOptions) setLTIMode(ltiOptions.ltiMode, ltiOptions)

    signInWithCustomToken(repository.auth, token)
      .then(async () => {
        const breakoutUser = await repository.waitForBreakoutUser()

        if (!breakoutUser.isLtiUser) {
          // refusing to log in without a lti user id
          // this is a security measure to prevent users from logging in
          // as direct breakout users. This is an expected condition, not an
          // error worth reporting to Sentry, so navigate away without
          // capturing an exception.
          if (validatedLTIMode) {
            rootStore.navigateToLtiErrorPage(tt.lti.login_error())
          }
          return
        }

        if (
          (breakoutUser.isFaculty || breakoutUser.isCorre) &&
          impersonateUserId
        ) {
          rootStore.impersonateUser({
            userId: impersonateUserId,
            hideBanner: true,
          })
        }

        if (landingPage) {
          rootStore.navigateToUrl(landingPage)
          return
        }

        rootStore.navigateTo(
          'instructorLibrary',
          undefined,
          redirectTo
            ? {
                redirectTo: redirectTo,
              }
            : undefined
        )
      })
      .catch((error) => {
        captureException(error)
        if (validatedLTIMode) {
          rootStore.navigateToLtiErrorPage(tt.lti.login_error())
        }
      })
  }, [
    deepLinkJwt,
    token,
    rootStore,
    repository.auth,
    redirectTo,
    landingPage,
    sectionId,
    setLTIMode,
    impersonateUserId,
    tt,
    repository,
    ltiOptions,
    validatedLTIMode,
    hasProcessedToken,
  ])

  return <FullPageSpinner reason="login_with_token_loading" />
}

function IframeProtector({ children }: { children: React.ReactNode }) {
  const queryParams = new URLSearchParams(window.location.search)
  const ejectFromIframe = queryParams.get('ejectFromIframe') === 'true'
  const { tt } = useTranslationTyped()
  const startTime = useMemo(() => Date.now(), [])
  const [isOutdated, setIsOutdated] = useState(false)

  useEffect(() => {
    const outdatedTime = 1000 * 60 * 15 // 15 minutes
    setInterval(() => {
      if (Date.now() - outdatedTime > startTime) {
        setIsOutdated(true)
      }
    }, 1000)
  }, [startTime])

  if (ejectFromIframe && areWeInIframe()) {
    return (
      <div className="lti-configuration min-h-screen w-screen md:-m-8">
        <div className="">
          <img
            src="/assets/images/logo.png"
            width={64}
            className="absolute right-8 top-8"
            alt={tt.breakout_learning()}
          />
        </div>
        <div className="lti-rocket flex min-h-screen w-full flex-col items-start justify-center">
          <div className="ml-[5vw] max-w-[400px]">
            <div className="mb-5">
              <h2 className="text-headline-small">
                {tt.lti.configuration.eject_headline_small()}
              </h2>
              <h1 className="text-headline-large">
                {tt.lti.configuration.eject_headline_large()}
              </h1>
              <div className="text-body-large">
                {tt.lti.configuration.eject_headline_description()}
              </div>
            </div>
            <div>
              <div>
                {isOutdated && (
                  <div className="flex h-fit min-h-[2.5rem] w-fit min-w-[9.5rem] select-none items-center justify-center rounded-xl bg-core-primary px-5 py-1 text-xs font-semibold leading-[normal] text-core-on-primary">
                    {tt.login.reload_application()}
                  </div>
                )}
                {!isOutdated && (
                  <BreakoutButtonLink
                    className="flex items-center justify-center"
                    kind="accent"
                    href={window.location.href.replace(
                      'ejectFromIframe=true',
                      ''
                    )}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {tt.login.open_application_in_new_tab()}
                  </BreakoutButtonLink>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
  return <>{children}</>
}
export function LoginWithToken() {
  const { tt } = useTranslationTyped()
  return (
    <IframeProtector>
      <div className="relative box-border flex h-full w-full items-center justify-center overflow-auto rounded-3xl bg-core-tertiary">
        <PageTitle title={tt.login.page_title()} />
        <TokenLogin />
      </div>
    </IframeProtector>
  )
}
